'use client';

import { AnimatePresence, motion } from 'framer-motion';
import type { PanInfo } from 'framer-motion';
import { useCallback, useEffect, useState } from 'react';

import type { TattooImage } from '@/types/tattooImage.types';

type ImageLightboxProps = {
  images: Array<TattooImage>;
  initialIndex: number;
  isOpen: boolean;
  onClose: () => void;
};

const ImageLightbox = ({ images, initialIndex, isOpen, onClose }: ImageLightboxProps) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [[page, direction], setPage] = useState([initialIndex, 0]);

  useEffect(() => {
    setCurrentIndex(initialIndex);
    setPage([initialIndex, 0]);
  }, [initialIndex]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const paginate = useCallback(
    (newDirection: number) => {
      const newIndex = currentIndex + newDirection;
      if (newIndex >= 0 && newIndex < images.length) {
        setPage([newIndex, newDirection]);
        setCurrentIndex(newIndex);
      }
    },
    [currentIndex, images.length],
  );

  const handleNext = useCallback(() => {
    if (currentIndex < images.length - 1) {
      paginate(1);
    }
  }, [currentIndex, images.length, paginate]);

  const handlePrev = useCallback(() => {
    if (currentIndex > 0) {
      paginate(-1);
    }
  }, [currentIndex, paginate]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowRight') {
        handleNext();
      } else if (e.key === 'ArrowLeft') {
        handlePrev();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, handleNext, handlePrev]);

  const handleDragEnd = (_e: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    const swipeThreshold = 50;
    const swipeVelocityThreshold = 500;

    if (
      info.offset.x < -swipeThreshold ||
      (info.velocity.x < -swipeVelocityThreshold && info.offset.x < 0)
    ) {
      handleNext();
    } else if (
      info.offset.x > swipeThreshold ||
      (info.velocity.x > swipeVelocityThreshold && info.offset.x > 0)
    ) {
      handlePrev();
    }
  };

  const imageVariants = {
    enter: (dir: number) => ({
      opacity: 0,
      x: dir > 0 ? 100 : -100,
    }),
    center: {
      zIndex: 1,
      opacity: 1,
      x: 0,
    },
    exit: (dir: number) => ({
      zIndex: 0,
      opacity: 0,
      x: dir < 0 ? 100 : -100,
    }),
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black md:bg-black/90 md:p-8"
          onClick={onClose}
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-[10000] flex h-10 w-10 items-center justify-center rounded-full bg-white/20 text-white backdrop-blur-sm transition-all hover:scale-110 hover:bg-white/30"
            aria-label="Fermer"
          >
            <svg
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <div className="absolute top-4 left-4 z-50 rounded-full bg-white/10 px-4 py-2 text-sm text-white backdrop-blur-sm">
            {currentIndex + 1} / {images.length}
          </div>

          {currentIndex > 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handlePrev();
              }}
              className="absolute left-4 z-50 rounded-full bg-white/10 p-3 text-white backdrop-blur-sm transition-colors hover:bg-white/20"
              aria-label="Image précédente"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
          )}

          {currentIndex < images.length - 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleNext();
              }}
              className="absolute right-4 z-50 rounded-full bg-white/10 p-3 text-white backdrop-blur-sm transition-colors hover:bg-white/20"
              aria-label="Image suivante"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          )}

          <div className="h-full w-full md:h-auto md:w-auto">
            <motion.div
              initial={{ scale: 0.9, y: window.innerWidth < 768 ? '100%' : 0 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: window.innerWidth < 768 ? '100%' : 0 }}
              transition={{ duration: 0.3, ease: [0.32, 0.72, 0, 1] }}
              className="relative h-full w-full overflow-hidden md:h-[90vh] md:w-[90vw] md:rounded-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <AnimatePresence initial={false} custom={direction} mode="wait">
                <motion.div
                  key={page}
                  custom={direction}
                  variants={imageVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{
                    duration: 0.2,
                    ease: 'easeOut',
                  }}
                  drag="x"
                  dragConstraints={{ left: 0, right: 0 }}
                  dragElastic={0.2}
                  onDragEnd={handleDragEnd}
                  className="absolute flex h-full w-full cursor-grab items-center justify-center active:cursor-grabbing"
                >
                  <div className="relative h-full w-full p-4">
                    <img
                      src={images[currentIndex].image_url}
                      alt={`Tatouage ${currentIndex + 1}`}
                      className="h-full w-full object-contain"
                    />
                  </div>
                </motion.div>
              </AnimatePresence>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ImageLightbox;
